import React, { useContext, useCallback, useState, useRef } from 'react';
import { Dialog, DialogContent, DialogActions, Button, DialogTitle, DialogContentText, TextField, makeStyles, Theme } from '@material-ui/core';
import { DatePicker } from '@material-ui/pickers';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import moment, { Moment } from 'moment/moment';
import clsx from 'clsx';
import { useModeFormStyle, useGenericDialogStyle, useScreenSizeCheck, capitalizeAll, getMidnightDate } from '@moderepo/mode-app-components';
import { ritzDataContext, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { useRitzTranslation } from '../../utils/customHooks';
import { LanguageSelectorContainer } from '../../containers/language-selector/LanguageSelectorContainer';


const useStyle = makeStyles((theme: Theme) => {
    return {
        form: {
            '& > *': {
                width: '100%',
            },
        },
        datePickersContainer: {
            display       : 'flex',
            alignItems    : 'center',
            justifyContent: 'space-between',
        },
        datePicker: {
            '&:first-child': {
                marginRight: theme.spacing(1),
            },
            '&:last-child': {
                marginLeft: theme.spacing(1),
            },
        },
        datePickerInput: {
            '&.hidden': {
                visibility : 'hidden',
                '&::before': {
                    content: '""',
                },
            },
        },
    };
}, { name: 'CreateLogRequestDialog' });



interface CreateLogRequestDialogProps {
    readonly projectId: number | undefined;
    readonly open: boolean;
    readonly onCancel: ()=> void;
    readonly onCreateRequest: (startDate: Date | undefined, endDate: Date | undefined, prefixes: string[] | undefined)=> void;
}



/**
 * This is a dialog used for creating a request to get a device's log
 */
export const CreateLogRequestDialog: React.FC<CreateLogRequestDialogProps> = (
    props: CreateLogRequestDialogProps,
) => {

    const { state:dataState } = useContext(ritzDataContext);
    const project = props.projectId ? selectProjectById(dataState, props.projectId) : undefined;
    const { trans } = useRitzTranslation(project);

    const formClasses = useModeFormStyle();
    const dialogClasses = useGenericDialogStyle();
    const classes = useStyle();

    const prefixesInputRef = React.useRef<HTMLInputElement>();
    const isMediumScreen = useScreenSizeCheck('md');
    const today = useRef<Date>(getMidnightDate(moment(Date.now()).toDate()));
    const tomorrow = useRef<Date>(moment(today.current).add(1, 'day').toDate());
    const [startDate, setStartDate] = useState<Date | undefined>(undefined);
    const [endDate, setEndDate] = useState<Date | undefined>(undefined);

    const maxStartDate = moment.min([today.current, endDate ? moment(endDate).subtract(1, 'day').toDate() : undefined]
        .filter((d: Date | undefined): boolean => {
            return d !== undefined;
        })
        .map((d: Date | undefined): Moment => {
            return moment(d);
        })).toDate();

    const minEndDate = startDate ? moment(startDate).add(1, 'day').toDate() : undefined;
    const maxEndDate = tomorrow.current;


    // Callback Handlers
    const onCancelHandler = props.onCancel;
    const onCreateLogRequestHandler = props.onCreateRequest;


    /**
     * On Close button click, call the onCancel handler
     */
    const onCloseButtonClicked = useCallback(() => {
        onCancelHandler();
    }, [onCancelHandler]);


    /**
     * On form submit. All input are optional so we don't need to validate them.
     */
    const onFormSubmit = useCallback(async (event: React.FormEvent) => {
        event.stopPropagation();
        event.preventDefault();

        const prefixesInput = (prefixesInputRef.current as HTMLInputElement).value.trim();

        onCreateLogRequestHandler(startDate, endDate, prefixesInput && prefixesInput.length > 0
            ? prefixesInput.split(',').map((prefix: string): string => {
                return prefix.trim();
            }).filter((prefix: string): boolean => {
                return prefix.length > 0;
            }) : undefined);

        return false;

    }, [onCreateLogRequestHandler, startDate, endDate]);


    return (
        <Dialog
            open={props.open}
            fullScreen={!isMediumScreen}
            fullWidth={isMediumScreen}
            disableEscapeKeyDown={false}
            onClose={(event, reason) => {
                // Don't close the dialog when backdrop clicked
                if (reason !== 'backdropClick') {
                    setStartDate(undefined);
                    props.onCancel();
                }
            }}
        >
            <form className={clsx(formClasses.root, classes.form)} noValidate autoComplete="on" method="POST" onSubmit={onFormSubmit}>
                <DialogTitle className={dialogClasses.dialogTitle}>
                    <div className={dialogClasses.dialogTitleText}>
                        {capitalizeAll(trans('dialog.create_log_request.title'))}
                    </div>
                    <LanguageSelectorContainer projectId={props.projectId} minimal />
                </DialogTitle>

                <DialogContent
                    className={dialogClasses.dialogContent}
                >
                    <DialogContentText className={dialogClasses.dialogMessage}>
                        {trans('dialog.create_log_request.message')}
                    </DialogContentText>
                </DialogContent>

                <DialogContent
                    className={dialogClasses.dialogContent}
                >
                    <div className={clsx(formClasses.formInputField, classes.datePickersContainer)}>
                        <DatePicker
                            className={clsx(classes.datePicker)}
                            fullWidth
                            format="ll"
                            label={capitalizeAll(trans('label.start_date'))}
                            todayLabel={capitalizeAll(trans('label.today'))}
                            okLabel={capitalizeAll(trans('label.ok'))}
                            cancelLabel={capitalizeAll(trans('label.cancel'))}
                            clearLabel={capitalizeAll(trans('label.clear'))}
                            maxDate={maxStartDate}
                            animateYearScrolling
                            showTodayButton
                            clearable
                            autoOk
                            value={startDate || (endDate && moment(endDate).subtract(1, 'day').toDate()) || today.current}
                            inputProps={{
                                className: `${classes.datePickerInput} ${startDate === undefined ? 'hidden' : ''}`,
                            }}
                            onChange={(date: MaterialUiPickersDate) => {
                                if (date) {
                                    // On start date change. Make sure startDate is BEFORE end date. If start date is AFTER end date,
                                    // make it 1 day before end date.
                                    const midnight = getMidnightDate(date.toDate());
                                    if (moment(midnight).isBefore(maxStartDate)) {
                                        setStartDate(midnight);
                                    } else {
                                        setStartDate(maxStartDate);
                                    }
                                } else {
                                    setStartDate(undefined);
                                }
                            }}
                        />
                        <DatePicker
                            className={clsx(classes.datePicker)}
                            fullWidth
                            format="ll"
                            label={capitalizeAll(trans('label.end_date'))}
                            todayLabel={capitalizeAll(trans('label.today'))}
                            okLabel={capitalizeAll(trans('label.ok'))}
                            cancelLabel={capitalizeAll(trans('label.cancel'))}
                            clearLabel={capitalizeAll(trans('label.clear'))}
                            minDate={minEndDate}
                            maxDate={maxEndDate}
                            animateYearScrolling
                            showTodayButton
                            clearable
                            autoOk
                            value={endDate || tomorrow.current}
                            inputProps={{
                                className: `${classes.datePickerInput} ${endDate === undefined ? 'hidden' : ''}`,
                            }}
                            onChange={(date: MaterialUiPickersDate) => {
                                if (date) {
                                    // On end date change. Make sure end date is AFTER start date. If end date is BEFORE start date,
                                    // make it 1 day after start date.
                                    const midnight = getMidnightDate(date.toDate());
                                    if (!minEndDate || moment(midnight).isAfter(minEndDate)) {
                                        setEndDate(midnight);
                                    } else {
                                        setEndDate(minEndDate);
                                    }
                                } else {
                                    setEndDate(undefined);
                                }
                            }}
                        />
                    </div>

                    <TextField
                        className={formClasses.formInputField}
                        variant="standard"
                        type="text"
                        fullWidth
                        label={capitalizeAll(trans('label.filename_prefixes'))}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        inputProps={{
                            ref        : prefixesInputRef,
                            placeholder: trans('input.filename_prefixes.placeholder'),
                        }}
                        helperText={trans('input.filename_prefixes.help_text')}
                    />

                </DialogContent>

                <DialogActions
                    className={dialogClasses.dialogActionContainer}
                >
                    <Button
                        onClick={onCloseButtonClicked}
                    >
                        {trans('label.cancel')}
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                    >
                        {trans('label.create')}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};
